import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy, doc, addDoc, deleteDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { Check, Trash2, Plus, Search, X } from "lucide-react";
import MySpaceSubmitForm from "../components/MySpaceSubmitForm";

export default function MySpace() {
  const [writings, setWritings] = useState([]);
  const [pendingWritings, setPendingWritings] = useState([]);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchWritings();
  }, []);

  const fetchWritings = async () => {
    try {
      const publishedQuery = query(collection(db, "myspace"), orderBy("createdAt", "desc"));
      const publishedSnapshot = await getDocs(publishedQuery);
      const publishedData = publishedSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setWritings(publishedData.filter(item => item.published !== false));

      const pendingQuery = query(collection(db, "myspacerequests"), orderBy("createdAt", "desc"));
      const pendingSnapshot = await getDocs(pendingQuery);
      const pendingData = pendingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPendingWritings(pendingData.filter(item => item.published === false));
    } catch (error) {
      console.error("Error fetching MySpace writings:", error);
    }
  };

  const handleApprove = async (id) => {
    try {
      const pendingDoc = pendingWritings.find(w => w.id === id);
      if (!pendingDoc) return;

      await addDoc(collection(db, "myspace"), {
        ...pendingDoc,
        published: true,
        publishedAt: new Date(),
      });

      await deleteDoc(doc(db, "myspacerequests", id));
      fetchWritings();
    } catch (error) {
      console.error("Error approving:", error);
      alert("Failed to approve writing");
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Are you sure you want to delete this submission?")) return;
    try {
      await deleteDoc(doc(db, "myspacerequests", id));
      fetchWritings();
    } catch (error) {
      console.error("Error deleting:", error);
      alert("Failed to delete writing");
    }
  };

  const filteredWritings = writings.filter(item => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      item.title?.toLowerCase().includes(q) ||
      item.author?.toLowerCase().includes(q) ||
      item.short?.toLowerCase().includes(q) ||
      item.full?.toLowerCase().includes(q) ||
      item.type?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen mt-20 bg-white p-6">
      <div className="max-w-3xl mx-auto mb-10 flex items-center justify-center gap-4">
        <button
          onClick={() => { setShowSearch(!showSearch); if (showSearch) setSearchQuery(""); }}
          className="bg-black hover:bg-gray-800 text-white p-3 rounded-full shadow-lg"
          title="Search Writings"
        >
          {showSearch ? <X size={28} /> : <Search size={28} />}
        </button>

        <button
          onClick={() => setShowSubmitForm(true)}
          className="bg-black hover:bg-gray-800 text-white p-3 rounded-full shadow-lg"
          title="Submit Your Writing"
        >
          <Plus size={28} />
        </button>

        <div className="text-center">
          <h1 className="text-4xl font-bold">MySpace</h1>
          <p className="text-lg mt-2">Selected writings from our community</p>
        </div>
      </div>

      {showSearch && (
        <div className="max-w-3xl mx-auto mb-6">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title, author, content..."
            className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:border-black focus:outline-none"
            autoFocus
          />
          {searchQuery && (
            <p className="mt-2 text-sm text-gray-600 text-center">
              Found {filteredWritings.length} result{filteredWritings.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      )}

      {/* Pending Admin Panel */}
      {showAdmin && pendingWritings.length > 0 && (
        <div className="max-w-4xl mx-auto mb-8 bg-yellow-50 border border-yellow-300 rounded-xl p-6">
          <h2 className="text-2xl font-bold mb-4">Pending Approvals ({pendingWritings.length})</h2>
          {pendingWritings.map(item => (
            <div key={item.id} className="bg-white border border-gray-300 rounded-lg p-6 mb-4">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                  <p className="text-sm mb-1">By <b>{item.author}</b> • {item.type}</p>
                  <p className="text-xs text-gray-600">{item.designation} • {item.college}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => handleApprove(item.id)} className="bg-green-600 text-white p-2 rounded-lg"><Check size={20} /></button>
                  <button onClick={() => handleReject(item.id)} className="bg-red-600 text-white p-2 rounded-lg"><Trash2 size={20} /></button>
                </div>
              </div>
              <div className="text-gray-800 whitespace-pre-wrap">{item.full}</div>
            </div>
          ))}
        </div>
      )}

      {pendingWritings.length > 0 && (
        <div className="max-w-4xl mx-auto mb-6 text-center">
          <button onClick={() => setShowAdmin(!showAdmin)} className="text-sm text-gray-600 hover:text-gray-900 underline">
            {showAdmin ? "Hide" : "Show"} Admin Panel ({pendingWritings.length} pending)
          </button>
        </div>
      )}

      {/* Published Writings */}
      <div className="max-w-4xl mx-auto grid gap-6">
        {filteredWritings.length > 0 ? filteredWritings.map(item => (
          <div
            key={item.id}
            onClick={() => navigate(`/myspace/${item.id}`)}
            className="border border-black rounded-xl p-6 cursor-pointer hover:bg-gray-50"
          >
            <h2 className="text-2xl font-semibold mb-1">{item.title}</h2>
            <p className="text-sm mb-2">By <b>{item.author}</b> • {item.type}</p>
            <p className="text-gray-700">{item.short}</p>
            <span className="inline-block mt-4 text-sm font-semibold underline">Read more →</span>
          </div>
        )) : searchQuery ? (
          <div className="text-center py-12 text-gray-500">
            <Search size={48} className="mx-auto mb-4 opacity-30" />
            <p className="text-xl">No results found for "{searchQuery}"</p>
          </div>
        ) : null}
      </div>

      {showSubmitForm && <MySpaceSubmitForm onClose={() => setShowSubmitForm(false)} />}
    </div>
  );
}
